import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@libs/zapi-ui/shadcn";
import { PlusIcon } from "@radix-ui/react-icons";
import { AppButton, AppInput, Typography } from "@libs/zapi-ui/custom";
import { ACCEPTED_FILE_TYPES, MAX_FILES, MAX_TOTAL_SIZE_BYTES } from "../../const/repositoryConsts";
import { FileArrowUpIcon, GlobeIcon } from "@phosphor-icons/react";
import { ChangeEvent, useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { validateFilesLimit } from "@/zapi-frontend/shared/lib/validateFilesLimit";

type TUploadFilesButtonProps = {
  createDocument: (file: File) => Promise<void>;
  createWebLink: (url: string) => Promise<void>;
  setUploadingDocuments: (files: string[]) => void;
  isDocumentsPending?: boolean;
  isDocumentsProcessing?: boolean;
};

export function UploadFilesButton(props: TUploadFilesButtonProps) {
  const { createDocument, createWebLink, setUploadingDocuments, isDocumentsPending, isDocumentsProcessing } = props;
  const attachmentFileInputRef = useRef<HTMLInputElement>(null);
  const [webLinkDialogOpen, setWebLinkDialogOpen] = useState(false);
  const [webLinkUrl, setWebLinkUrl] = useState("");
  const [isAddingWebLink, setIsAddingWebLink] = useState(false);

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        const files = Array.from(e.target.files);

        const errorMessage = validateFilesLimit({
          files,
          maxFiles: MAX_FILES,
          maxTotalSizeBytes: MAX_TOTAL_SIZE_BYTES
        });
        if (errorMessage) {
          toast.error(errorMessage);

          if (attachmentFileInputRef.current) {
            attachmentFileInputRef.current.value = "";
          }

          return;
        }

        setUploadingDocuments(files.map((f) => f.name));

        try {
          await Promise.all(files.map(createDocument));
        } finally {
          setUploadingDocuments([]);
          // reset the input so the same file can be re‑uploaded if needed
          if (attachmentFileInputRef.current) {
            attachmentFileInputRef.current.value = "";
          }
        }
      }
    },
    [createDocument, setUploadingDocuments]
  );

  const handleAddWebLink = useCallback(async () => {
    if (!webLinkUrl.trim()) return;

    let url = webLinkUrl.trim();
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      url = `https://${url}`;
    }

    try {
      new URL(url);
    } catch {
      toast.error("Please enter a valid URL");
      return;
    }

    setIsAddingWebLink(true);
    try {
      await createWebLink(url);
      setWebLinkDialogOpen(false);
      setWebLinkUrl("");
      toast.success("Web link added and processing started");
    } catch {
      toast.error("Failed to add web link");
    } finally {
      setIsAddingWebLink(false);
    }
  }, [webLinkUrl, createWebLink]);

  const isBusy = isDocumentsPending || isDocumentsProcessing;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild data-testid="file-item-upload-files-fab" className="mt-auto ml-auto mb-2 mr-2">
          <AppButton
            size="icon"
            className="rounded-full p-0"
            disabled={isBusy}
            startAdornment={<PlusIcon width={20} height={20} />}
            data-testid="upload-files-fab"
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="px-1 mr-5">
          <Tooltip>
            <TooltipTrigger className="w-full">
              <DropdownMenuItem
                onClick={() => attachmentFileInputRef.current?.click()}
                variant="default"
                className="cursor-pointer font-medium px-3 py-2"
                data-testid="upload-files-item"
              >
                <FileArrowUpIcon className="w-4 h-4 text-black" />
                <Typography variant="label2">Files</Typography>
              </DropdownMenuItem>
            </TooltipTrigger>
            <TooltipContent>
              <Typography variant="body3">
                {isBusy
                  ? "Wait for current uploads to finish"
                  : `Upload up to ${MAX_FILES} files (max ${MAX_TOTAL_SIZE_BYTES / 1024 / 1024}MB total)`}
              </Typography>
            </TooltipContent>
          </Tooltip>
          <DropdownMenuItem
            onClick={() => setWebLinkDialogOpen(true)}
            variant="default"
            className="cursor-pointer font-medium px-3 py-2"
            data-testid="add-web-link-item"
          >
            <GlobeIcon className="w-4 h-4 text-black" />
            <Typography variant="label2">Web Link</Typography>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <input
        type="file"
        ref={attachmentFileInputRef}
        multiple
        accept={ACCEPTED_FILE_TYPES}
        style={{ display: "none" }}
        onChange={handleFileChange}
        data-testid="upload-files-input"
      />

      <Dialog
        open={webLinkDialogOpen}
        onOpenChange={(open) => {
          if (!isAddingWebLink) {
            setWebLinkDialogOpen(open);
            if (!open) setWebLinkUrl("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Web Link</DialogTitle>
          </DialogHeader>
          <AppInput
            label="URL"
            placeholder="https://example.com"
            value={webLinkUrl}
            onChange={(e) => setWebLinkUrl(e.target.value)}
            disabled={isAddingWebLink}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddWebLink();
            }}
          />
          <DialogFooter>
            <AppButton
              appearance="outlined"
              color="zinc150"
              onClick={() => {
                setWebLinkDialogOpen(false);
                setWebLinkUrl("");
              }}
              disabled={isAddingWebLink}
            >
              Cancel
            </AppButton>
            <AppButton onClick={handleAddWebLink} isLoading={isAddingWebLink} disabled={!webLinkUrl.trim()}>
              Add
            </AppButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
